import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {DocumentFragment} from 'app/fragment/types';
import {Breadcrumb} from 'app/interfaces';
import {DocumentService} from 'app/services/document.service';
import {SectionService} from 'app/services/section.service';
import {ViewMode} from 'app/view/current-view';
import {CurrentLocation, ViewService} from 'app/view/view.service';
import {from, Observable} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ReferenceSummaryResolverService implements Resolve<void> {
  private static REFERENCE_SUMMARY_TITLE: string = 'CARS Reference Summary';

  constructor(
    private _viewService: ViewService,
    private _sectionService: SectionService,
    private _documentService: DocumentService
  ) {}

  public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<void> {
    const currentView = this._viewService.getCurrentView();
    this._viewService.setLocation(CurrentLocation.REFERENCE_SUMMARY);
    this._viewService.setCurrentView(ViewMode.REFERENCE_SUMMARY, currentView?.versionTag);
    this._sectionService.setSelected(null);

    const document: DocumentFragment = this._documentService.getSelected();
    const breadcrumbs: Breadcrumb[] = this._generateBreadcrumbs(document, route);
    route.data = {breadcrumbs: breadcrumbs};
    return from(Promise.resolve());
  }

  private _generateBreadcrumbs(document: DocumentFragment, route: ActivatedRouteSnapshot): Breadcrumb[] {
    const urlSegments: Map<string, string> = new Map();

    // If document title and section title overlay the path can become corrupt.
    // We add an invisible space to the section title when the document title
    // and section title are the same.
    const sectionTitle =
      document.title === ReferenceSummaryResolverService.REFERENCE_SUMMARY_TITLE
        ? ReferenceSummaryResolverService.REFERENCE_SUMMARY_TITLE + ' '
        : ReferenceSummaryResolverService.REFERENCE_SUMMARY_TITLE;

    if (this._isTopLevelRoute(route)) {
      urlSegments.set('Documents', 'documents');
      urlSegments.set(document.title, document.id.value);
    }

    urlSegments.set(sectionTitle, `reference-summary`);

    const breadcrumbs: Breadcrumb[] = [];
    let link: string = this._isTopLevelRoute(route) ? '' : route.parent.url.map((seg) => seg.path).join('/');

    urlSegments.forEach((urlSegment: string, title: string) => {
      link += '/' + urlSegment;
      breadcrumbs.push({title: title, link: link});
    });

    return breadcrumbs;
  }

  /**
   * Determines whether this is a child of a document-level route or not.
   *
   * @param route {ActivatedRouteSnapshot} The route.
   * @returns     {boolean}                True if this is not a child route.
   */
  private _isTopLevelRoute(route: ActivatedRouteSnapshot): boolean {
    return route.paramMap.has('document_id');
  }
}
